import {
	BillingFeature,
	DeliveryType,
	GoalOwnerScheduleType,
	ScheduleType,
} from 'webapp/enums/enums';
import {
	NotificationFilterFrequency,
	NotificationFilterType,
	NotificationScheduleType,
} from 'webapp/enums/notifications';
import { compact } from 'webapp/utils/common';

import type { Goal } from 'webapp/models-custom';
import NotificationBase from 'webapp/models-custom/notification/base';

export default class NotificationGoal extends NotificationBase {
	type = NotificationFilterType.GOAL;
	targetObject: Goal;
	typeColor = 'var(--databox-green-500)';
	typeIcon: IconName = 'Target';

	override billingFeature = BillingFeature.GOALS;

	constructor(model: Goal) {
		super();
		this.targetObject = model;
	}

	get title() {
		return this.targetObject.title;
	}

	get source() {
		const goal = this.targetObject;
		return goal.isManualGoal ? null : this.targetObject.sourceCM;
	}

	get subtitleText() {
		return this.source?.title || '';
	}

	override get subtitleImage() {
		return this.source?.realAvatar || '';
	}

	get isMine() {
		return this.targetObject.isMine;
	}

	override get isValidForList() {
		return super.isValidForList && this.targetObject.isDeliveryOn;
	}

	get scheduleType() {
		const goal = this.targetObject;
		if (goal.isManualGoal && goal.isMine) {
			return NotificationScheduleType.REMINDER;
		} else {
			const frequency = this.frequency;
			if (
				frequency.length === 1 &&
				frequency[0] === NotificationFilterFrequency.STATUS_CHANGE
			) {
				return NotificationScheduleType.ALERT;
			}
		}
		return NotificationScheduleType.UPDATE;
	}

	get frequency() {
		const goal = this.targetObject;
		if (goal.isManualGoal) {
			if (goal.isMine) {
				switch (this.targetObject.ownerScheduleType) {
					case GoalOwnerScheduleType.WEEKLY:
						return [NotificationFilterFrequency.WEEKLY];
					case GoalOwnerScheduleType.BIWEEKLY:
						return [NotificationFilterFrequency.BIWEEKLY];
					case GoalOwnerScheduleType.MONTHLY:
						return [NotificationFilterFrequency.MONTHLY];
					case GoalOwnerScheduleType.QUARTERLY:
						return [NotificationFilterFrequency.QUARTERLY];
					default:
						return [];
				}
			}
		} else {
			const frequency: NotificationFilterFrequency[] = [];
			if (goal.scheduleTypes.includes(ScheduleType.ONE_OFF)) {
				frequency.push(NotificationFilterFrequency.STATUS_CHANGE);
			}
			goal.scheduleTypes.forEach((o) => {
				const f = this.mmNotificationsService.getFrequencyFromSchedule(o);
				if (f) {
					frequency.push(f);
				}
			});
			return frequency;
		}

		return [];
	}

	get frequencyTooltipTextTop() {
		return this.targetObject.humanReadableConditionText;
	}

	get createdBy() {
		return null; // we have only owner
	}

	get createdOn() {
		return null; // have created but thats for goal itself
	}

	get deliveryTypes(): DeliveryType[] {
		if (this.targetObject.isManualGoal) {
			return [DeliveryType.EMAIL];
		} else {
			return compact(
				this.targetObject.delivery.map((d) => {
					switch (d) {
						case DeliveryType.EMAIL:
							return DeliveryType.EMAIL;
						case DeliveryType.SLACK:
							return DeliveryType.SLACK;
						default:
							return null;
					}
				}),
			);
		}
	}

	get emailRecipients() {
		// cast existing emails into user ids
		const users = this.mmUsersService.activatedUserRecords;
		return this.targetObject.recipients.map((email) => {
			const userId = users.find((u) => u.email === email)?.id;
			return userId ? +userId : email;
		});
	}

	get allRecipients() {
		return this.targetObject.mappedRecipients;
	}

	delete() {
		const goal = this.targetObject;
		if (this.isMine) {
			goal.isDeliveryOn = false;
		} else {
			const email = this.currentUserService.model?.email || '';
			goal.recipients = goal.recipients.filter((e) => e !== email);
		}
		void goal.saveAndCatch();
	}
}
